import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

import { ref, onMounted, watchEffect, toRaw, watch, getCurrentInstance, computed } from 'vue';
import { useRoute } from 'vue-router';
import { configRegistry } from '@/lib/configRegistry';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import { useFloatFormStore } from '@/store/floatForm';
import type { FormField, FormProps } from '@/types/form';
import debounce from 'lodash.debounce';


export default /*@__PURE__*/_defineComponent({
  __name: 'CreateForm',
  setup(__props) {

const route = useRoute();
const floatFormStore = useFloatFormStore();
const moduleName =floatFormStore.module;

const props = floatFormStore.props as FormProps;
const onCreate = props?.onCreate;
const formTitle = floatFormStore.formTitle;

const formFields = ref<FormField[]>([]);
const steps = ref<Array<{ title?: string; description?: string; fields: FormField[] }>>([]);
const currentStep = ref(0);
const hasSteps = computed(() => steps.value.length > 0);
const allFields = computed<FormField[]>(() =>
  hasSteps.value ? steps.value.flatMap(step => step.fields) : formFields.value
);
const currentStepFields = computed<FormField[]>(() =>
  hasSteps.value ? steps.value[currentStep.value]?.fields || [] : formFields.value
);
const form = ref<Record<string, any>>({});
const formValid = ref(true);
const nestedValid = ref(true);
const formRef = ref();
const visibleFields = ref<FormField[]>([]);

function useCan() {
  const instance = getCurrentInstance();
  return instance?.appContext.config.globalProperties.$can as (perm: string) => boolean;
}

const $can = useCan();

watchEffect(() => {
  visibleFields.value = currentStepFields.value
    .map((field) => {
      // Permiso primero
      if (field.permission) {
        const hasPermission = $can(field.permission);
        if (!hasPermission) {
          // El permiso no lo tiene, revisa qué hacer
          if (field.permission_state === 'hidden') return null;
          if (field.permission_state === 'disabled') return { ...field, disabled: true };
        }
      }

      // Dependencias
      const val = form.value[field.dependsOn as any];
      const condition = field.dependsCondition;
      let conditionMet = true;

      if (field.dependsOn && field.dependsBehavior) {
        switch (condition?.condition) {
          case 'diferent':
            conditionMet = val !== undefined && val !== '' && val !== condition.value;
            break;
          case 'equals':
            conditionMet = val === condition.value;
            break;
          case 'includes':
            conditionMet = !JSON.stringify(val ?? '')?.toLowerCase?.().includes(condition.value);
            break;
        }

        if (field.dependsBehavior === 'hidden' && !conditionMet) return null;
        if (field.dependsBehavior === 'disabled' && !conditionMet) {
          return { ...field, disabled: true };
        }
      }

      return field;
    })
    .filter((f): f is FormField => !!f)
    .filter(f => !!f.component);
});

const debouncedFetchers = {} as any;

const getFetchParams = (field: FormField, form: any, val: string = '') => {
  let params: Record<string, any> = {};
  if (field.searchValue && val!='') params[`filter[${field.searchValue}]`] = val;
  if (typeof field.fetchParams === 'function') {
    params = { ...params, ...field.fetchParams(form) };
  }
  return params;
};

const fieldLoading = ref<Record<string, boolean>>({});

const getFieldProps = (field: FormField) => {

  const props: Record<string, any> = {
    label: field.label,
    placeholder: field.placeholder ?? '',
    clearable: true,
    density: 'compact',
    type: field.type || 'text',
    prefix: field.prefix,
    suffix: field.suffix,
    'prepend-inner-icon': field.icon,
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
    multiple: field.multiple || false,
    'item-title': field.itemText || 'label',
    'item-value': field.itemValue || 'value',
    'hide-no-data': true,
    range:field.range,
    module:field.module,
    ...field.props,
    loading: !!fieldLoading.value[field.value],
  };

  // items SIEMPRE debe ser una referencia reactiva del field
  props.items = field.options || [];

  // fetch dinámico:
  if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
    const fetchFn = remoteOptionsRegistry[field.fetchOptions as any];

    if (!debouncedFetchers[field.value]) {
      debouncedFetchers[field.value] = debounce(async (val: string) => {
        fieldLoading.value[field.value] = true;
        const params = getFetchParams(field, form.value, val);
        const results = await fetchFn(params);
        field.options = Array.isArray(results) ? [...results] : [];
        fieldLoading.value[field.value] = false;
      }, 500);
    }

    props['onFocus'] = (open: boolean) => {
      if (open && (!field.options || field.options.length === 0)) {
        fieldLoading.value[field.value] = true;
        // Fetch sin search al abrir (ejemplo: muestra todo, o hazlo configurable)
        fetchFn(getFetchParams(field, form.value, '')).then(results => {
          field.options = Array.isArray(results) ? [...results] : [];
          fieldLoading.value[field.value] = false;
        });
      }
    };

    props['onUpdate:search'] = (val: string) => {
      /*if (typeof val === 'string' && val.trim().length < 2) {
        field.options = [];
        return;
      }*/
      debouncedFetchers[field.value](val);
    };
  }
  return props;
};

const onNestedValidation = (valid: boolean) => {
  nestedValid.value = valid;
};

const resetForm = () => {
  fieldLoading.value = {};
  const resetField = (f: FormField) => {
    if (f.layoutOnly && f.children) {
      f.children.forEach(resetField);
      return;
    }

    if (f.isJsonArray && f.plainJson && f.children) {
      const resetGroup: Record<string, any> = {};
      f.children.forEach((c) => {
        const defaultVal = c.multiple ? [] : '';
        form.value[c.value] = defaultVal;
        resetGroup[c.value] = defaultVal;
      });
      form.value[f.value] = resetGroup;
      return;
    }

    form.value[f.value] = f.multiple ? [] : '';
  };

  allFields.value.forEach(resetField);
};

const closeForm = () => {
  resetForm();
  floatFormStore.closeForm();
};

const flattenForm = (form: Record<string, any>, fields: FormField[]): Record<string, any> => {
  const flatForm: Record<string, any> = {};

  for (const field of fields) {
    const val = form[field.value];

    // --- 0. Campos de solo layout: procesa hijos directamente sin agregar key
    if (field.layoutOnly && field.children) {
      Object.assign(flatForm, flattenForm(form, field.children as FormField[]));
      continue;
    }

    // --- 1. Si hay transform, mergea resultado (no sobrescribe otros casos)
    let transformResult: any = undefined;
    if (typeof field.transform === 'function') {
      transformResult = field.transform(toRaw(val), form);
      if (typeof transformResult === 'object' && transformResult !== null && !Array.isArray(transformResult)) {
        Object.assign(flatForm, transformResult); // agrega key(s) custom
      } else if (transformResult !== undefined) {
        flatForm[field.value] = transformResult;
      }
      // OJO: NO return ni continue, porque puede requerir merge con children/array
    }

    // --- 2. JsonArray + children
    if (field.isJsonArray && field.children) {
      if (typeof field.transform === 'function') {
        // 👈🔥 SI HAY TRANSFORM, USALO EN LUGAR DE map
        const result = field.transform(toRaw(val), form);
        flatForm[field.value] = result;
        continue;
      }
      if (Array.isArray(val)) {
        // Array de objetos (DynamicJsonField, etc)
        flatForm[field.value] = val.map((row: any) => flattenForm(row, field.children as FormField[]));
      }
      else if (field.plainJson) {
        for (const child of field.children) {
          const childVal = val?.[child.value] ?? null;
          if (typeof child.transform === 'function') {
            const transformed = child.transform(childVal, val);
            if (
              typeof transformed === 'object' &&
              transformed !== null &&
              !Array.isArray(transformed)
            ) {
              Object.assign(flatForm, transformed);
            } else if (transformed !== undefined) {
              flatForm[child.value] = transformed;
            }
          } else {
            flatForm[child.value] = childVal;
          }
        }
      }
      else if (typeof val === 'object' && val !== null) {
        // Un objeto tipo grouped panel NO plain
        flatForm[field.value] = flattenForm(val, field.children as FormField[]);
      }
      continue; // Ya procesaste este caso
    }

    // --- 3. Multiple simple (no isJsonArray)
    if (field.multiple && Array.isArray(val)) {
      flatForm[field.value] = val.map((v: any) =>
        typeof v === 'object' ? v[field.itemValue || 'value'] ?? v.id ?? null : v
      );
      continue;
    }

    // --- 4. Objeto simple (select/autocomplete sin transform)
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      flatForm[field.value] = val[field.itemValue || 'value'] ?? val.id ?? null;
      continue;
    }

    // --- 5. Valor plano (si no se seteo ya por transform)
    if (
      transformResult === undefined // <-- si ya se asignó arriba, no sobreescribas
      && flatForm[field.value] === undefined
    ) {
      flatForm[field.value] = val ?? null;
    }
  }

  return flatForm;
};


const submitForm = async () => {
  if (!formRef.value?.validate() || !nestedValid.value) return;

  try {
  const payload = flattenForm(form.value, allFields.value);

    if (onCreate) {
      await onCreate(payload);   // Si hay error, va al catch y no cierra el form
      closeForm();               // Solo se cierra si todo salió bien
    } else {
      console.warn('[CreateForm] No se proporcionó función onCreate');
    }

  } catch (err) {
    // Aquí manejas el error visual, alert, etc.
    // El form NO se cierra si hay error
    console.error('[CreateForm] Error en onCreate', err);
  }
};

onMounted(async () => {
  const loader = moduleName?configRegistry[moduleName]:false;
  if (!loader) {
    console.error(`[CreateForm] Config para ${moduleName} no encontrada.`);
    return;
  }

  try {
    const mod = await loader();
    const maybeSteps = mod.CREATE_STEPS || mod.CREATE_FORM_STEPS;
    if (Array.isArray(maybeSteps) && maybeSteps.length) {
      steps.value = maybeSteps;
      formFields.value = steps.value.flatMap((s: any) => s.fields || []);
    } else {
      formFields.value = mod.CREATE_FORM || [];
    }
    
    const initialValues = props.initialValues ?? {};

    const setupInitialField = (field: FormField) => {
      if (field.layoutOnly && field.children) {
        field.children.forEach(setupInitialField);
        return;
      }

      const initial = initialValues[field.value];

      if (field.isJsonArray && field.plainJson && field.children) {
        const group: Record<string, any> = {};
        field.children.forEach(child => {
          const childInitial = initialValues[child.value];
          const fallback = child.multiple ? [] : '';
          form.value[child.value] = childInitial ?? fallback;
          group[child.value] = childInitial ?? fallback;
        });
        form.value[field.value] = initial ?? group ?? (field.multiple ? [] : '');
      } else {
        form.value[field.value] = initial ?? (field.multiple ? [] : '');
      }

      if ((field.component as any)?.name === 'VSelect' || field.component === 'VSelect') {
        if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
          const fetchFn = remoteOptionsRegistry[field.fetchOptions];
          // Llama una sola vez para cargar las opciones
          fetchFn('').then(results => {
            field.options = Array.isArray(results) ? [...results] : [];
          });
        }
      }
    };

    allFields.value.forEach(setupInitialField);

  } catch (err) {
    console.error('[CreateForm] Error al cargar config:', err);
  }

  // ----------------------
  // GENÉRICO: Watchers para campos dependientes
  // ----------------------
  allFields.value
    .filter(field => !!field.dependsOn)
    .forEach(field => {
      watch(
        () => form.value[field.dependsOn as string],
        async (newValue) => {
          // Evalúa la condición (igual que en visibleFields)
          let conditionMet = true;
          const condition = field.dependsCondition;
          switch (condition?.condition) {
            case 'diferent':
              conditionMet = newValue !== undefined && newValue !== '' && newValue !== condition.value;
              break;
            case 'equals':
              conditionMet = newValue === condition.value;
              break;
            case 'includes':
              conditionMet = !JSON.stringify(newValue ?? '')?.toLowerCase?.().includes(condition.value);
              break;
            default:
              conditionMet = !!newValue;
          }
          if (conditionMet) {
            // fetchOptions
            if (field.fetchOptions) {
              const fetchFn = remoteOptionsRegistry[field.fetchOptions];
              const params = getFetchParams(field, form.value, '');
              const results = await fetchFn(params);
              field.options = Array.isArray(results) ? [...results] : [];
            }
          } else {
            field.options = [];
            form.value[field.value] = null;
          }
        },
        { immediate: true }
      );
    });
});

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    onSubmit: _withModifiers(submitForm, ["prevent"]),
    ref_key: "formRef",
    ref: formRef,
    modelValue: formValid.value,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formValid).value = $event))
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, { class: "px-5 py-3" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center mb-3" }, {
            default: _withCtx(() => [
              _createElementVNode("span", null, _toDisplayString(_unref(formTitle)), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_text, { style: {"scrollbar-width":"none"} }, {
            default: _withCtx(() => [
              _createVNode(_component_v_row, null, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleFields.value, (field) => {
                    return (_openBlock(), _createElementBlock(_Fragment, {
                      key: field?.value
                    }, [
                      field
                        ? (_openBlock(), _createBlock(_component_v_col, {
                            key: 0,
                            cols: "12",
                            md: field.col || 12,
                            sm: field.mdcol || 12
                          }, {
                            default: _withCtx(() => [
                              (!field.isJsonArray)
                                ? (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), _mergeProps({
                                    key: 0,
                                    modelValue: form.value[field.value],
                                    "onUpdate:modelValue": ($event: any) => ((form.value[field.value]) = $event)
                                  }, { ref_for: true }, getFieldProps(field), {
                                    rules: field.required ? [v => !!v || 'Campo obligatorio'] : [],
                                    disabled: field.disabled,
                                    variant: "solo-filled"
                                  }), null, 16, ["modelValue", "onUpdate:modelValue", "rules", "disabled"]))
                                : (field.layoutOnly)
                                  ? (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), {
                                      key: 1,
                                      "model-value": form.value,
                                      "onUpdate:modelValue": _cache[0] || (_cache[0] = val => Object.assign(form.value, val)),
                                      fields: field.children,
                                      summary: field.summary,
                                      label: field.label,
                                      "layout-only": field.layoutOnly,
                                      form: form.value,
                                      onValidation: onNestedValidation,
                                      disabled: field.disabled,
                                      variant: "solo-filled"
                                    }, null, 40, ["model-value", "fields", "summary", "label", "layout-only", "form", "disabled"]))
                                  : (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), {
                                      key: 2,
                                      variant: "solo-filled",
                                      modelValue: form.value[field.value],
                                      "onUpdate:modelValue": ($event: any) => ((form.value[field.value]) = $event),
                                      fields: field.children,
                                      summary: field.summary,
                                      label: field.label,
                                      "layout-only": field.layoutOnly,
                                      form: form.value,
                                      onValidation: onNestedValidation,
                                      disabled: field.disabled
                                    }, null, 40, ["modelValue", "onUpdate:modelValue", "fields", "summary", "label", "layout-only", "form", "disabled"]))
                            ]),
                            _: 2
                          }, 1032, ["md", "sm"]))
                        : _createCommentVNode("", true)
                    ], 64))
                  }), 128))
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                variant: "text",
                onClick: closeForm
              }, {
                default: _withCtx(() => _cache[4] || (_cache[4] = [
                  _createTextVNode("Cancelar")
                ])),
                _: 1,
                __: [4]
              }),
              (hasSteps.value && currentStep.value > 0)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 0,
                    variant: "text",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (currentStep.value = currentStep.value - 1))
                  }, {
                    default: _withCtx(() => _cache[5] || (_cache[5] = [
                      _createTextVNode(" Anterior ")
                    ])),
                    _: 1,
                    __: [5]
                  }))
                : _createCommentVNode("", true),
              (hasSteps.value && currentStep.value < steps.value.length - 1)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 1,
                    color: "primary",
                    variant: "tonal",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (currentStep.value = currentStep.value + 1))
                  }, {
                    default: _withCtx(() => _cache[6] || (_cache[6] = [
                      _createTextVNode(" Siguiente ")
                    ])),
                    _: 1,
                    __: [6]
                  }))
                : _createCommentVNode("", true),
              _createVNode(_component_v_btn, {
                type: "submit",
                disabled: !formValid.value || !nestedValid.value || (hasSteps.value && currentStep.value < steps.value.length - 1),
                color: "primary"
              }, {
                default: _withCtx(() => _cache[7] || (_cache[7] = [
                  _createTextVNode(" Guardar ")
                ])),
                _: 1,
                __: [7]
              }, 8, ["disabled"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})